/* eslint-disable no-unused-vars */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import clsx from 'clsx';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  makeStyles,
  Typography,
  Chip,
  Checkbox,
  Button
} from '@material-ui/core';
import { EcomOrder } from 'src/types';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import {
  AlternatingColorTableRow,
  LinkComponent,
  LoaderBar
} from 'src/components';
import { formatCurrency } from 'src/utils';
import { useNavigate } from 'react-router-dom';
import { colors } from 'src/constants';
import { unwrapResult } from '@reduxjs/toolkit';
import {
  MultiSelectPurchaseOrderPayload,
  PurchaseOrderListItem
} from 'src/redux/slices/purchase-order/types';
import {
  allowedRoles,
  purchaseOrderStatusOptions,
  purchaseOrderStatusOptionsV2,
  purchaseOrderTableHeaders,
  roleBasedTransitions,
  roles,
  statusButtonTextMap,
  statusOptionsPerRole
} from 'src/redux/slices/purchase-order';
import { useIsMobile } from 'src/hooks/use-is-mobile';
import { Alert, Pagination } from '@material-ui/lab';
import { useRoles, useSnackBar } from 'src/hooks';
import { getStatusTransitionPayload } from 'src/utils/purchase-order/status-transition';
import { canUserTransition } from 'src/utils/purchase-order/can-user-transition';
import useUserInfo from 'src/hooks/user/use-user-info';
import PurchaseOrderStatusField from './PurchaseOrderStatusSelect';

const {
  selectors: purchaseOrderSelectors,
  actions: purchaseOrderActions
} = slices.purchaseOrder;

interface Props {
  className?: string;
}

const useStyles = makeStyles(() => ({
  root: {},
  imgPreview: {
    width: undefined,
    height: 50
  },
  priceInput: {
    height: 30
  },
  lightGrey: {
    backgroundColor: colors.blueGrey[50]
  },
  table: {
    border: '1px solid #a4abba'
  },
  tableCell: {
    textAlign: 'center',
    borderBottom: '1px solid #a4abba'
  },
  tableRow: {
    borderBottom: '1px solid #a4abba'
  }
}));

const component = ({ className, ...rest }: Props) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const snackbar = useSnackBar();
  const { getRoles } = useRoles();
  const { userDetails } = useUserInfo();

  const userInfo = getRoles(...roles);

  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  // const [isVisible, setIsVisible] = useState<Boolean>(true);
  const initialStatusRef = useRef<string>('');

  const purchaseOrders = useAppSelector(
    purchaseOrderSelectors.selectPurchaseOrders
  );
  const isShowList = useAppSelector(purchaseOrderSelectors.selectIsShowList);

  const purchaseOrdersParams = useAppSelector(
    purchaseOrderSelectors.selectPurchaseOrderParams
  );

  const purchaseOrderMeta = useAppSelector(
    purchaseOrderSelectors.selectPurchaseOrderMeta
  );

  const isLoading = useAppSelector(
    purchaseOrderSelectors.selectPurchaseOrderLoading
  );

  const selectedPurchaseOrders = useAppSelector(
    (state) => state.purchaseOrder.selectedPurchaseOrders
  );

  const isOwner = useMemo(() => {
    return userDetails?.all_roles?.some((role) => role.name === 'owner');
  }, [userDetails]);

  const isForDeliveryProductIn = useMemo(
    () =>
      purchaseOrdersParams.status === 'for_delivery' ||
      purchaseOrdersParams.status === 'for_product_in',
    [purchaseOrdersParams.status]
  );

  const userRoleNames = (userInfo || [])
    .map((role) => role?.name)
    .filter(Boolean);

  const onOrderPress = (item: PurchaseOrderListItem) => {
    alert('Order Pressed Navigate to its details');
    // navigate(`/app/ecom-order/${item?.order_ref_no}`, { state: item });
  };

  // const getPurchaseOrders = async () => {
  //   dispatch(purchaseOrderActions.getPurchaseOrdersThunk({}));
  // };

  const poStatusColor = useCallback((status?: string) => {
    const selectedColor = purchaseOrderStatusOptionsV2?.find(
      (option) => option.value === status
    )?.color;
    return selectedColor || colors.black[500];
  }, []);

  // useEffect(() => {
  //   getPurchaseOrders();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  const onHandlePageChange = async (page_num?: number) => {
    await dispatch(
      purchaseOrderActions.getPurchaseOrdersThunk({
        ...purchaseOrdersParams,
        page: page_num
      })
    );
  };

  const handleSelectRows = useCallback(
    (selected: PurchaseOrderListItem[]) => {
      dispatch(purchaseOrderActions.setSelectedPurchaseOrder(selected));
    },
    [dispatch]
  );

  const handleClearSelection = useCallback(() => {
    setSelectedIds([]);
    dispatch(purchaseOrderActions.clearSelectedPurchaseOrder());
  }, [dispatch]);

  const handleSelectAll = useCallback(() => {
    const allIds =
      purchaseOrders
        ?.filter((po) => typeof po.po_id === 'number')
        ?.map((po) => Number(po.po_id)) ?? [];

    setSelectedIds(allIds);
    dispatch(purchaseOrderActions.setSelectedPurchaseOrder(purchaseOrders));
  }, [purchaseOrders, dispatch]);

  const toggleRowSelection = (item: PurchaseOrderListItem) => {
    if (typeof item.po_id !== 'number') return;

    let updated: number[];
    if (selectedIds.includes(item.po_id)) {
      updated = selectedIds.filter((id) => id !== item.po_id);
    } else {
      updated = [...selectedIds, item.po_id];
    }

    setSelectedIds(updated);

    const selectedData = purchaseOrders.filter(
      (po) => typeof po.po_id === 'number' && updated.includes(po.po_id)
    );

    handleSelectRows(selectedData);
  };

  const searchPo = async () => {
    dispatch(
      purchaseOrderActions.getPurchaseOrdersThunk({
        status: initialStatusRef.current
      })
    );
  };

  const handleSubmitForApproval = async () => {
    const payload = {
      po_ids: selectedIds,
      current_status: initialStatusRef.current,
      new_status: selectedStatus
    };

    if (!payload) return;

    const relevantStatuses = new Set<string>();
    const relevantRoles = new Set<string>();
    userDetails?.all_roles?.forEach(({ name }) => {
      if (name && statusOptionsPerRole[name]) {
        statusOptionsPerRole[name].forEach((status) => {
          relevantStatuses.add(status);
          relevantRoles.add(name);
        });
      }
    });

    const isAllAllowed = canUserTransition(
      initialStatusRef.current,
      payload.new_status,
      relevantRoles
    );

    if (!isAllAllowed && !isOwner) {
      snackbar.show({
        severity: 'error',
        message:
          'You do not have permission to update one or more of the selected Purchase Orders.'
      });
      return;
    }

    try {
      const response = unwrapResult(
        await dispatch(
          purchaseOrderActions.updateMultiplePurchaseOrdersThunk({
            ...payload,
            is_owner: isOwner ? 1 : 0
          })
        )
      );

      if (!response?.success) {
        const { message, errors } = response;
        snackbar.show({
          severity: 'error',
          message: errors
            ? Object.values(errors).join(', ')
            : message || `Failed to update Purchase Orders`
        });
        return;
      }

      snackbar.show({
        severity: 'success',
        message: 'Purchase Orders updated successfully'
      });
    } catch (error) {
      console.error('Error during status update:', error);
      snackbar.show({
        severity: 'error',
        message: 'An unexpected error occurred while submitting'
      });
    } finally {
      handleClearSelection();
      searchPo();
    }
  };

  useEffect(() => {
    const status = purchaseOrders[0]?.status;

    if (status) {
      setSelectedStatus(status);
      initialStatusRef.current = status;
    } else {
      initialStatusRef.current = '';
      setSelectedStatus('');
    }
  }, [purchaseOrders]);

  useEffect(() => {
    if (isLoading) handleClearSelection();
  }, [handleClearSelection, isLoading]);

  return isShowList ? (
    <Card
      className={clsx(classes.root, className)}
      {...rest}
      style={{
        borderRadius: 0
      }}
    >
      <Box style={{ padding: 10 }}>
        <Box display="flex" style={{ gap: 10, marginBottom: 10 }}>
          {selectedIds.length > 0 && (
            <>
              <PurchaseOrderStatusField
                id="status"
                fullWidth={false}
                value={selectedStatus}
                currentValue={initialStatusRef.current}
                onChange={(v) => setSelectedStatus(v ? v.value : '')}
                isDetailsPage={true}
              />

              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmitForApproval}
              >
                Update
              </Button>
            </>
          )}

          {purchaseOrders.length > 0 && !isForDeliveryProductIn ? (
            <Typography
              variant="overline"
              color="textPrimary"
              style={{ marginTop: 3, marginLeft: 10 }}
            >
              {` ${selectedIds.length} item(s) selected`}
            </Typography>
          ) : null}
        </Box>
        {isForDeliveryProductIn ? null : (
          <Box display="flex" style={{ gap: 10 }}>
            {selectedIds.length > 0 && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClearSelection}
              >
                Unselect All
              </Button>
            )}
            {!purchaseOrdersParams.status ||
            selectedIds.length >= purchaseOrders.length ? null : (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleSelectAll}
              >
                Select All
              </Button>
            )}
          </Box>
        )}
      </Box>

      <LoaderBar isLoading={isLoading} />
      {purchaseOrders.length > 0 ? (
        <PerfectScrollbar>
          <Box
            minWidth={1050}
            style={{
              minHeight: purchaseOrders.length != 0 ? 700 : 0,
              maxHeight: purchaseOrders.length != 0 ? 700 : 200,
              overflowY: 'scroll',
              overflowX: isMobile ? 'scroll' : 'hidden'
            }}
          >
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  {purchaseOrderTableHeaders.map((header, idx) => (
                    <TableCell key={idx} className={classes.tableCell}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {purchaseOrders?.map((item, i: number) => (
                  <AlternatingColorTableRow
                    hover
                    key={i}
                    selected={
                      typeof item.po_id === 'number' &&
                      selectedIds.includes(item.po_id)
                    }
                  >
                    <TableCell
                      className={classes.tableCell}
                      style={{ width: 10 }}
                    >
                      {item?.status !== 'for_delivery' &&
                      item?.status !== 'for_product_in' ? (
                        <Checkbox
                          checked={
                            typeof item.po_id === 'number' &&
                            selectedIds.includes(item.po_id)
                          }
                          disabled={
                            ['for_delivery', 'for_product_in'].includes(
                              initialStatusRef.current ?? ''
                            ) ||
                            (initialStatusRef.current === 'for_approval' &&
                              !userRoleNames.some(
                                (role) =>
                                  typeof role === 'string' &&
                                  allowedRoles.includes(role)
                              ))
                          }
                          onChange={() => toggleRowSelection(item)}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <LinkComponent
                        onClick={() => onOrderPress(item)}
                        href={`/app/purchase-order/${item?.po_id}`}
                        title={`${item?.order_no}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                      {/* TODO: Add color on status and make it a chip*/}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography>{item?.branch || '--'}</Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography>{item?.supplier || '--'}</Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography>{item?.po_date}</Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography>{item?.delivery_date || '--'}</Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography>{item?.amount}</Typography>
                    </TableCell>
                    <TableCell
                      className={classes.tableCell}
                      style={{ maxWidth: 200 }}
                    >
                      <Typography>{item?.categories}</Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography>{item?.created_by}</Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography>{item?.for_tax_certificate}</Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography
                        variant="subtitle2"
                        style={{
                          marginLeft: 10,
                          color: poStatusColor(item?.status),
                          borderColor: poStatusColor(item?.status)
                        }}
                      >
                        {purchaseOrderStatusOptionsV2.find(
                          (opt) => opt.value === item?.status
                        )?.label ||
                          item?.status ||
                          '--'}
                      </Typography>
                    </TableCell>
                  </AlternatingColorTableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Box display="flex" justifyContent="flex-end" p={2}>
            {!isLoading ? (
              <Pagination
                count={purchaseOrderMeta?.last_page || 1}
                page={purchaseOrderMeta?.current_page || 1}
                onChange={(e, p) => onHandlePageChange(p)}
              />
            ) : null}
          </Box>
        </PerfectScrollbar>
      ) : null}
    </Card>
  ) : null;
};

export const PurchaseOrderListTable = React.memo(component);
